/* eslint-disable @typescript-eslint/ban-ts-comment */
import { FC, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  Button,
  ModalIcon,
  Spacer,
  Stack,
  TextBody,
  TextTitle,
  Input,
  ButtonGroup,
  Icon,
  Card,
} from '@pypestream/design-system';
import { TranslationComponent } from '@pypestream/translations';
import { PuffLoader } from 'react-spinners';

import { GenericModal, GenericModalProps } from '..';
import {
  sendProStudioEvent,
  useProStudioCtxSelector,
  useProStudioStateMatches,
} from '../../../xstate/app.xstate';
import { useProStudioRoutes } from '../../../hooks';
import { broadcastSolutionDeleted } from '../../../stores/utils/solution-sync-channel';
import { resolveDeleteSolutionTarget } from '../../../utils/solution-open-failure';

export type DeleteSolutionModalProps = GenericModalProps & {
  preferOpenFailure?: boolean;
  /** Runs from the modal `close` event, including Cancel. Not from the confirm click itself. */
  onClosed?: () => void;
};

export const DeleteSolutionModal: FC<DeleteSolutionModalProps> = ({
  modalRef,
  onClose: onCloseProp,
  onClosed,
  preferOpenFailure,
  testId = 'delete-solution-modal',
}) => {
  const navigate = useNavigate();
  const { home } = useProStudioRoutes();
  const { draftVersionSolutionId, botName, projectId, solutionName } =
    useProStudioCtxSelector((ctx) => {
      const target = resolveDeleteSolutionTarget({
        solutions: ctx.solutions,
        selectedDisplayName: ctx.selectedBot?.displayName,
        botName: ctx.botName,
        openFailure: ctx.solutionOpenFailure,
        preferOpenFailure,
      });

      return {
        draftVersionSolutionId: target?.id,
        botName: target?.displayName,
        // PSTUDIO-3671: the machine's solution key (solution `name`, NOT
        // displayName) + projectId — the exact pair the receiving tab's
        // useSolutionDeletedSync compares against.
        projectId: ctx.project?.id,
        solutionName: target?.solutionName,
      };
    });

  const isDeleting = useProStudioStateMatches(
    'checkingUserInfo.projectFlow.projectDataLoaded.botFlow.deletingBotFlow'
  );

  const [inputValue, setInputValue] = useState('');
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  const resetInputError = () => {
    setErrorMessage('');
    setHasError(false);
  };

  const resetInput = () => {
    setInputValue('');
    resetInputError();
  };

  const onClose = () => {
    resetInput();
    setIsClosing(false);
    onCloseProp?.();
    onClosed?.();
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (errorMessage || hasError) {
      resetInputError();
    }

    setInputValue(e.target.value);
  };

  const handleDelete = () => {
    if (!draftVersionSolutionId) {
      return;
    }

    if (inputValue !== botName) {
      setHasError(true);
      setErrorMessage('Please verify the solution name.');
      return;
    }

    sendProStudioEvent({
      type: 'deleteBot',
      id: draftVersionSolutionId,
      cb: () => {
        setIsClosing(true);
        // Don't call onClose() here - it would reset isClosing immediately
        // Just close the modal and keep the loading state visible during animation
        onCloseProp?.();
        // PSTUDIO-3671: tell sibling tabs of THIS browser the solution is gone,
        // so a tab with it open ejects immediately (no reload needed).
        if (projectId && solutionName) {
          broadcastSolutionDeleted({ projectId, botName: solutionName });
        }
        navigate(home.path);
      },
    });
  };

  if (isDeleting || isClosing) {
    return (
      <GenericModal
        test-id={testId}
        modalRef={modalRef}
        onClose={onClose}
        onError={() => resetInputError()}
        hideCloseBtn={isDeleting}
        stayOnClickOutside={isDeleting}
        stayOnEsc={isDeleting}
      >
        <Stack alignItems="center" justifyContent="center" direction="column">
          <TextTitle size="small" textAlign="center">
            Deleting Solution
          </TextTitle>
          <Spacer size="small" />
          <div>
            <PuffLoader color="#5956E3" size={60} speedMultiplier={0.7} />
          </div>
          <Spacer size="medium" />
          <TextBody variant="secondary" textAlign="center" size="small">
            Removing solution data and cleaning up resources...
          </TextBody>
          <Card
            size="medium"
            shadow="none"
            border="default"
            style={{
              background: '#f0f8ff',
              borderColor: '#e3f2fd',
              padding: '20px',
            }}
          >
            <Stack alignItems="center" gutter="small">
              <Icon name="info" size="small" color="purple" />
              <TextBody
                variant="secondary"
                textAlign="center"
                size="small"
                style={{ color: '#1976d2' }}
              >
                Please keep this window open until the process completes
              </TextBody>
            </Stack>
          </Card>
        </Stack>
      </GenericModal>
    );
  }

  return (
    <GenericModal
      test-id={testId}
      modalRef={modalRef}
      onClose={onClose}
      onError={() => resetInputError()}
      hideCloseBtn={isDeleting}
      stayOnClickOutside={isDeleting}
    >
      <Stack justifyContent="center">
        <ModalIcon name="error" />
      </Stack>
      <TextTitle size="small" textAlign="center">
        <TranslationComponent i18nKey="pro-studio/navigation:delete_solution_modal.title">
          Delete this solution?
        </TranslationComponent>
      </TextTitle>
      <Spacer size="small" />
      <TextBody
        test-id={`${testId}-subtitle`}
        variant="secondary"
        textAlign="center"
      >
        <TranslationComponent i18nKey="pro-studio/navigation:delete_solution_modal.subtitle.part1">
          Deleting
        </TranslationComponent>{' '}
        {botName}{' '}
        <TranslationComponent i18nKey="pro-studio/navigation:delete_solution_modal.subtitle.part2">
          deletes all associated data. This does not affect solutions in other
          Projects.
        </TranslationComponent>
      </TextBody>
      <Spacer size="large" />
      <TextBody test-id={`${testId}-verify-text`} variant="secondary">
        <TranslationComponent i18nKey="pro-studio/navigation:delete_solution_modal.verify.part1">
          To verify, type
        </TranslationComponent>{' '}
        <span color="warning">&ldquo;{botName}&rdquo;</span>{' '}
        <TranslationComponent i18nKey="pro-studio/navigation:delete_solution_modal.verify.part2">
          below
        </TranslationComponent>
      </TextBody>
      <Spacer size="2xsmall" />
      <Input
        test-id={`${testId}-input`}
        variant="outlined"
        value={inputValue}
        onChange={handleInputChange}
        // @ts-ignore
        onInput={handleInputChange}
        hasError={hasError}
        helpText={errorMessage}
        disabled={isDeleting}
        name="solution-name"
      />
      <ButtonGroup slot="footer">
        <Button
          test-id={`${testId}-cancel-button`}
          width="full"
          variant="secondary"
          size="large"
          onClick={onClose}
          disabled={isDeleting}
        >
          <TranslationComponent i18nKey="pro-studio/navigation:delete_solution_modal.buttons.decline">
            Cancel
          </TranslationComponent>
        </Button>
        <Button
          test-id={`${testId}-delete-button`}
          width="full"
          variant="warning"
          size="large"
          onClick={handleDelete}
          disabled={isDeleting}
        >
          <TranslationComponent i18nKey="pro-studio/navigation:delete_solution_modal.buttons.accept">
            Delete
          </TranslationComponent>
        </Button>
      </ButtonGroup>
    </GenericModal>
  );
};
